/**
 * Tours de novidades exibidos pelo FeatureTourCmp na primeira visita a cada tela.
 *
 * - `target` e o valor do atributo `data-tour="..."` no template. Passo cujo alvo nao
 *   existe na tela (pedido cancelado nao tem caminhao, produto simples nao tem
 *   variacoes...) e pulado sozinho; o primeiro elemento com o atributo e o usado.
 * - Mudou a tela de novo e quer que todo mundo veja? Suba a `version`.
 * - `icon` e o nome de um icone ja registrado no App.vue.
 */
export interface FeatureTourHighlight {
    icon:string|string[]
    text:string
}

export interface FeatureTourStep {
    target:string
    title:string
    text:string
}

export interface FeatureTour {
    id:string
    version:number
    intro:{
        eyebrow:string
        title:string
        text:string
        highlights:FeatureTourHighlight[]
    }
    steps:FeatureTourStep[]
}

export const ORDER_LIST_TOUR:FeatureTour = {
    id: 'order-list',
    version: 1,
    intro: {
        eyebrow: 'Novo no Muven',
        title: 'Seus pedidos ganharam cara nova',
        text: 'A lista de pedidos foi redesenhada para você achar o que precisa num piscar de olhos.',
        highlights: [
            {icon: 'stream', text: 'Pedidos agrupados por dia, com o total vendido'},
            {icon: 'truck-moving', text: 'A jornada de cada pedido direto na lista'},
            {icon: ['far', 'copy'], text: 'Número do pedido com botão de copiar'},
            {icon: 'chart-line', text: 'Um resumo no rodapé: total, ticket médio e vendas por canal'},
        ],
    },
    steps: [
        {
            target: 'order-view-mode',
            title: 'Do seu jeito',
            text: 'Escolha entre a linha do tempo, agrupada por dia, ou a lista corrida de sempre. O Muven lembra da sua escolha.',
        },
        {
            target: 'order-day',
            title: 'Cada dia, um resumo',
            text: 'O cabeçalho do dia mostra quantos pedidos entraram e quanto foi vendido. Passe o mouse no valor para ver o detalhe.',
        },
        {
            target: 'order-ident',
            title: 'Canal e número juntos',
            text: 'a logomarca mostra de onde veio a venda. Clique no ícone ao lado do número para copiá-lo.',
        },
        {
            target: 'order-status',
            title: 'Onde está o pedido?',
            text: 'Passe o mouse no status para ver a jornada completa: em que etapa ele está e o que falta.',
        },
        {
            target: 'order-ledger',
            title: 'O resumo sempre à vista',
            text: 'O rodapé fica preso no pé da tela e soma os pedidos carregados: quantidade, ticket médio, descontos e total vendido, sem frete e sem cancelados. Quando houver mais de um canal ativo, passe o mouse na barra colorida para ver quanto cada canal vendeu.',
        },
    ],
}

export const ORDER_DETAIL_TOUR:FeatureTour = {
    id: 'order-detail',
    version: 1,
    intro: {
        eyebrow: 'Novo no Muven',
        title: 'O pedido, de ponta a ponta',
        text: 'O detalhe do pedido foi refeito para contar a história da venda numa tela só.',
        highlights: [
            {icon: 'truck-moving', text: 'Acompanhe o caminho do pedido até o cliente'},
            {icon: 'box', text: 'Produtos com foto e um recibo fácil de conferir'},
            {icon: 'user', text: 'Contatos do cliente prontos para copiar'},
        ],
    },
    steps: [
        {
            target: 'order-hero',
            title: 'Tudo no topo',
            text: 'Status, número copiável, datas e as ações do pedido - atualizar no canal, auditoria e marcar como entregue - ficam aqui.',
        },
        {
            target: 'order-journey',
            title: 'A jornada do pedido',
            text: 'Veja em que etapa o pedido está, da aprovação até a entrega. O caminhão anda conforme ele avança.',
        },
        {
            target: 'order-items',
            title: 'Produtos e valores',
            text: 'Cada item com foto, quantidade e preço, e logo abaixo o recibo com frete, descontos e total.',
        },
        {
            target: 'order-customer',
            title: 'Quem comprou',
            text: 'Documento, e-mail e telefone do cliente com botão de copiar, para agilizar o atendimento.',
        },
    ],
}

export const PRODUCT_DETAIL_TOUR:FeatureTour = {
    id: 'product-detail',
    version: 1,
    intro: {
        eyebrow: 'Novo no Muven',
        title: 'Um novo jeito de ver seus produtos',
        text: 'O detalhe do produto foi reorganizado para mostrar primeiro o que importa.',
        highlights: [
            {icon: 'images', text: 'Imagens em destaque, com visualizador ampliado'},
            {icon: 'link', text: 'A situação do produto em cada canal'},
            {icon: 'server', text: 'Dados da retaguarda explicados'},
        ],
    },
    steps: [
        {
            target: 'product-hero',
            title: 'O produto em destaque',
            text: 'Clique na imagem para ampliar e navegar pelas fotos. SKU e código da retaguarda podem ser copiados direto dos selos.',
        },
        {
            target: 'product-channels',
            title: 'Canais lado a lado',
            text: 'Cada cartão confere se o canal reconhece o produto associado. Dá para trocar a associação por aqui mesmo.',
        },
        {
            target: 'product-variations',
            title: 'Variações organizadas',
            text: 'Clique numa variação para abrir os detalhes dela: códigos, estoque e preço.',
        },
        {
            target: 'product-backend',
            title: 'Direto da retaguarda',
            text: 'Os dados que vêm do seu sistema de gestão. Os "?" explicam cada informação.',
        },
    ],
}
